import { collection, doc, getDoc, getDocs, query, where } from "firebase/firestore";
import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import FormUnavailable from "../../../components/FormUnavailable";
import FullPageLoader from "../../../components/FullPageLoader";
import SubmissionConfirmation from "../../../components/SubmissionConfirmation";
import { db } from "../../../firebase";
import { getFormByRoute } from "../../../services/formService";
import { getInventoryCatalog, getInventoryItem, listInventoryItems as listInventorySvc } from "../../../services/inventoryService";
import { submitLead as submitLeadSvc } from "../../../services/leadService";
import { getDefaultTheme, getThemeById as getThemeByIdSvc } from "../../../services/themeService";
import { isSubscriptionAccessAllowed } from "../../../utils/subscriptionAccess";
import { uploadToFirebaseStorage } from "../../../utils/FirebaseStorageUpload";
import { convertHeicFiles, convertHeicToJpeg } from "../../../utils/heicConverter";
import FormPreview from "./FormPreview";

/**
 * Load business and verify subscription allows public forms.
 * Does not change form documents.
 * @param {string} businessId
 * @return {Promise<{ok: boolean, businessName: string|null}>}
 */
async function checkBusinessSubscription(businessId) {
    if (!businessId) return { ok: false, businessName: null };
    const businessSnap = await getDoc(doc(db, "businesses", businessId));
    if (!businessSnap.exists()) return { ok: false, businessName: null };
    const data = businessSnap.data();
    return {
        ok: isSubscriptionAccessAllowed(data),
        businessName: data.businessName || null,
    };
}

const FormViewer = () => {
    const { formId, businessSlug, formSlug } = useParams();
    const [searchParams] = useSearchParams();
    const isPreview = searchParams.get('preview') === 'true';
    const [form, setForm] = useState(null);
    const [formData, setFormData] = useState({});
    const [loading, setLoading] = useState(true);
    const [inventoryItems, setInventoryItems] = useState([]);
    const [inventoryLoading, setInventoryLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [theme, setTheme] = useState(null);
    const [actualFormId, setActualFormId] = useState(null);
    const [subscriptionBlocked, setSubscriptionBlocked] = useState(false);
    const [blockedBusinessName, setBlockedBusinessName] = useState(null);

    useEffect(() => {
        let cancelled = false;

        const fetchFormAndInventory = async () => {
            const reservedRoutes = ['signin', 'signup', 'dashboard', 'form', 'profile', 'settings', 'home', 'www'];

            try {
                let formDocId = null;
                let loadedForm = null;

                if (businessSlug && formSlug && !reservedRoutes.includes(businessSlug.toLowerCase()) && !reservedRoutes.includes(formSlug.toLowerCase())) {
                    const routed = await getFormByRoute(businessSlug, formSlug);
                    if (routed) {
                        formDocId = routed.id;
                        loadedForm = routed;
                    } else {
                        const formsQuery = query(
                            collection(db, "forms"),
                            where("businessSlug", "==", businessSlug),
                            where("formSlug", "==", formSlug)
                        );
                        const formsSnap = await getDocs(formsQuery);
                        if (formsSnap.empty) {
                            console.warn("Form not found for slug:", businessSlug, formSlug);
                            alert("Form not found.");
                            window.location.href = "/404";
                            return;
                        }
                        const formDoc = formsSnap.docs[0];
                        formDocId = formDoc.id;
                        loadedForm = formDoc.data();
                    }
                } else if (!formId) {
                    console.warn("Missing formId — skipping fetch");
                    return;
                } else {
                    const isWebsiteUrl = /^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(formId) &&
                        (formId.includes('www.') || formId.includes('.com') ||
                            formId.includes('.co.') || formId.includes('.org') ||
                            formId.includes('.net') || formId.includes('.za') ||
                            formId.includes('.uk') || formId.includes('.ca'));

                    if (isWebsiteUrl) {
                        console.warn("FormId appears to be a website URL, redirecting...");
                        const websiteUrl = formId.startsWith('http') ? formId : `https://${formId}`;
                        window.location.href = websiteUrl;
                        return;
                    }

                    const snap = await getDoc(doc(db, "forms", formId));
                    if (!snap.exists()) {
                        console.warn("Form not found");
                        alert("Form not found.");
                        window.location.href = "/404";
                        return;
                    }
                    formDocId = formId;
                    loadedForm = snap.data();
                }

                if (cancelled || !loadedForm) return;
                setActualFormId(formDocId);

                if (loadedForm.status !== "published") {
                    if (loadedForm.businessWebsite) {
                        alert("This form is not yet published. Redirecting to the business website...");
                        window.location.href = loadedForm.businessWebsite;
                    } else {
                        alert("This form is not yet published and no website is available.");
                        window.location.href = "/404";
                    }
                    return;
                }

                const businessId = loadedForm.businessId;

                setForm({
                    ...loadedForm,
                    steps: loadedForm.steps?.map(step => {
                        if (step.label === "Product Name" && !step.dependsOn) {
                            return { ...step, dependsOn: "Category" };
                        }
                        if (step.label === "Primary Variant" && !step.dependsOn) {
                            return { ...step, dependsOn: "Product Name" };
                        }
                        return step;
                    }) || []
                });
                setInventoryLoading(Boolean(businessId));
                if (!cancelled) setLoading(false);

                const [themeResult, subCheck, catalog] = await Promise.all([
                    loadedForm.themeId && businessId
                        ? getThemeByIdSvc(businessId, loadedForm.themeId).catch((themeErr) => {
                            console.error("Error loading theme:", themeErr);
                            return null;
                        })
                        : Promise.resolve(null),
                    !isPreview
                        ? checkBusinessSubscription(businessId)
                        : Promise.resolve({ ok: true, businessName: null }),
                    businessId ? getInventoryCatalog(businessId) : Promise.resolve(null),
                ]);

                if (cancelled) return;

                if (!isPreview && !subCheck.ok) {
                    setBlockedBusinessName(subCheck.businessName);
                    setSubscriptionBlocked(true);
                    setInventoryLoading(false);
                    return;
                }

                if (themeResult) {
                    setTheme({
                        ...themeResult,
                        logoUrl: themeResult.logo,
                        backgroundImageUrl: themeResult.backgroundImage
                    });
                } else if (loadedForm.themeId) {
                    console.warn("Theme not found");
                }

                if (Array.isArray(catalog)) {
                    setInventoryItems(catalog);
                    setInventoryLoading(false);
                } else if (businessId) {
                    // Catalog is built from the inventory dashboard. Until it exists, load the full list
                    // without holding the rest of the form on that download.
                    listInventorySvc(businessId)
                        .then((items) => {
                            if (!cancelled) setInventoryItems(items.map(d => ({ ...d })));
                        })
                        .catch((err) => console.error("Error fetching inventory:", err))
                        .finally(() => {
                            if (!cancelled) setInventoryLoading(false);
                        });
                } else {
                    setInventoryLoading(false);
                }
            } catch (err) {
                console.error("Error fetching form:", err);
                alert("Something went wrong while loading the form.");
                window.location.href = "/404";
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetchFormAndInventory();
        return () => {
            cancelled = true;
        };
    }, [formId, businessSlug, formSlug, isPreview]);

    const selectedProductName = formData["Product Name"];

    useEffect(() => {
        const businessId = form?.businessId;
        if (!selectedProductName || !businessId) return;

        const summary = inventoryItems.find(item =>
            item.productName?.toLowerCase().trim() === String(selectedProductName).toLowerCase().trim()
        );
        if (!summary?.id || Array.isArray(summary.items)) return;

        let cancelled = false;
        getInventoryItem(businessId, summary.id)
            .then((full) => {
                if (cancelled) return;
                setInventoryItems(prev => prev.map(item => {
                    if (item.id !== summary.id) return item;
                    if (!full) return { ...item, items: [] };
                    return { ...item, ...full, items: Array.isArray(full.items) ? full.items : [] };
                }));
            })
            .catch((err) => console.error("Error loading product details:", err));

        return () => {
            cancelled = true;
        };
    }, [selectedProductName, form?.businessId, inventoryItems]);


    const handleChange = (key, value) => {
        setFormData(prev => ({ ...prev, [key]: value }));
    };

    const handleSubmit = async (data) => {
        // Disable submission in preview mode
        if (isPreview) {
            toast.info("🚫 This is a preview. Submissions are disabled.");
            return;
        }

        if (!form?.businessId) {
            console.warn("Missing businessId — cannot submit");
            return;
        }

        setSubmitting(true);

        const cleanedData = { ...data };

        // ✅ Upload image(s) if present - handle both single File and array of Files
        const imageField = data["Upload Image"];
        if (imageField) {
            try {
                // Fetch business name from business document
                let businessName = "default";
                if (form.businessId) {
                    try {
                        const businessRef = doc(db, "businesses", form.businessId);
                        const businessSnap = await getDoc(businessRef);
                        if (businessSnap.exists()) {
                            businessName = businessSnap.data().businessName || "default";
                        }
                    } catch (err) {
                        console.warn("Could not fetch business name, using default:", err);
                    }
                }
                
                // Handle both single file and array of files
                if (Array.isArray(imageField)) {
                    // Multiple files: convert HEIC->JPEG before upload and store as array of URLs
                    const filesToUpload = imageField.filter(file => file instanceof File);
                    const convertedFiles = await convertHeicFiles(filesToUpload);
                    const uploadPromises = convertedFiles
                        .map(file => uploadToFirebaseStorage(file, "Lead_Images", businessName));
                    
                    const imageUrls = await Promise.all(uploadPromises);
                    cleanedData["Upload Image"] = imageUrls.filter(url => url); // Filter out any failed uploads
                } else if (imageField instanceof File) {
                    // Single file: convert HEIC->JPEG before upload (backward compatibility)
                    const convertedFile = await convertHeicToJpeg(imageField);
                    const imageUrl = await uploadToFirebaseStorage(convertedFile, "Lead_Images", businessName);
                    if (!imageUrl) {
                        throw new Error("Failed to get image URL");
                    }
                    cleanedData["Upload Image"] = imageUrl;
                }
            } catch (err) {
                console.error("Image upload failed:", err);
                alert("Failed to upload image(s). Please try again.");
                setSubmitting(false);
                return;
            }
        }

        try {
            const formIdToUse = actualFormId || formId;
            await submitLeadSvc(form.businessId, formIdToUse, cleanedData);
            setSubmitted(true);
        } catch (err) {
            console.error("❌ Failed to submit form:", err);
            const msg = err?.message || "";
            if (msg.includes("temporarily unavailable")) {
                alert(msg);
            } else {
                alert("Something went wrong while submitting. Please try again.");
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) return <FullPageLoader />;
    if (subscriptionBlocked) {
        return <FormUnavailable businessName={blockedBusinessName} />;
    }
    if (submitting) return <FullPageLoader text="Submitting form..." />;
    if (submitted) return <SubmissionConfirmation form={form} theme={theme || getDefaultTheme()} />;
    if (!form?.steps) return <p>This form has no fields defined.</p>;

    return (
        <FormPreview
            steps={form.steps}
            formData={formData}
            onChange={handleChange}
            inventoryItems={inventoryItems}
            inventoryLoading={inventoryLoading}
            formMode={isPreview ? "draft" : form.status}
            theme={theme || getDefaultTheme()}
            onSubmit={handleSubmit}
            showSteps={form.showSteps || false}
            stepIndicatorStyle={form.stepIndicatorStyle || "progress"}
            formStyle={form.formStyle || "typeform"}
        />
    );
};

export default FormViewer;
